import { Injectable } from '@angular/core';
import { geometry, GradientStop, Group, LinearGradient, Rect, Surface } from '@progress/kendo-drawing';

import { ESRI } from '../esri-loader-util';
import { LayerService } from '../services/layer.service';
import { HeatmapConfig, HeatmapMode, IMapcoreFeatureLayer, IMapcoreMapImageLayer } from '../types';

const { Rect: RectGeometry, Point, Size, transform } = geometry;

let HeatmapRenderer: __esri.HeatmapRendererConstructor;

@Injectable({
    providedIn: 'root'
})

export class HeatmapService {
    isInitialized = false;
    private readonly heatmapConfig: HeatmapConfig;
    constructor(private readonly layerService: LayerService) {
        this.heatmapConfig = new HeatmapConfig(); }
    tempLayers: any[] = [];
    initializeHeatmapSymbol(heatmapRenderer: __esri.HeatmapRenderer) {
        const canvas = document.createElement('canvas'),
            ctx = canvas.getContext('2d'),
            grd = ctx.createLinearGradient(0, 0, 20, 0);
        let i = 0;
        for (const colorStop of heatmapRenderer.colorStops) {
            grd.addColorStop(i++, colorStop.color.toString());
        }
        ctx.fillStyle = grd;
        ctx.fillRect(0, 5, 20, 20);

    }
    async getHeatmapRenderer(renderer = null) {
        HeatmapRenderer = await ESRI.HeatmapRenderer;
        let heatmapRenderer: __esri.HeatmapRenderer;
        if (renderer) {
            heatmapRenderer = new HeatmapRenderer(renderer);
        } else {
            heatmapRenderer = new HeatmapRenderer(this.heatmapConfig.defaultOptions);
        }
        return heatmapRenderer;

    }
    async getEsriColor(color) {
        const Color = await ESRI.Color;
        if (!color) {
            return null;
        } else if (color.declaredClass === 'esri.Color') {
            return color;
        } else if (typeof color === 'string') {
            if (color.indexOf('rgb') === 0) {
                return Color.fromRgb(color);
            } else {
                return Color.fromString(color);
            }
        } else if (Array.isArray(color)) {
            return new Color(color);
        } else if (color.r !== undefined && color.g !== undefined && color.b !== undefined) {
            return new Color([color.r, color.g, color.b, color.a]);
        } else {
            return new Color({});
        }
    }
    public async getColorString(color: string, background = true, transparent =  false) {
        const Color = await ESRI.Color;
        if (background) {
            const c = Color.fromString(color).toRgba();
            if (transparent) {
                c[3] = 0;
            } else {
                c[3] = 1;
            }
            return `rgba(${c.join(',')})`;
        } else {
            return `rgb(${Color.fromString(color).toRgb().join(',')})`;
        }
    }
    public async updateHeatmapLayers(layer: IMapcoreMapImageLayer | IMapcoreFeatureLayer, heatmapRenderer = null) {
        let newLayer = null;
        const heatmapMode = layer.mapcoreProps.heatmapMode;
        if (layer && layer.type === 'map-image' && heatmapMode !== HeatmapMode.Off) {
            if ((layer as IMapcoreMapImageLayer).sublayers.length === 1) {
                newLayer = await this.layerService.convertToFeatureLayer(layer);
                newLayer.mapcoreProps.heatmapRenderer = await this.getHeatmapRenderer(heatmapRenderer);
                newLayer.mapcoreProps.renderer = newLayer.renderer;
                if (this.layerService.checkIfHeatmapIsOnOrOff(newLayer)) {
                    newLayer.renderer = newLayer.mapcoreProps.heatmapRenderer;
                }
                newLayer.mapcoreProps.url = undefined;
                const index = this.layerService.getLayerIndex(layer);
                this.layerService.removeLayer(layer);
                this.layerService.addLayer(newLayer, index);
                return newLayer;
            }
        } else if (layer && heatmapMode === HeatmapMode.Off && layer.type === 'feature') {
            newLayer = await this.layerService.convertToImagelayer(layer as IMapcoreFeatureLayer);
            this.layerService.removeLayerById(layer.id);
            newLayer.mapcoreProps.heatmapMode = heatmapMode;
            return newLayer;
        } else if (layer && heatmapMode === HeatmapMode.On && layer.mapcoreProps.renderer) {
            layer.mapcoreProps.heatmapMode = heatmapMode;
            if (!layer.mapcoreProps.heatmapRenderer) {
                (layer as IMapcoreFeatureLayer).mapcoreProps.heatmapRenderer = await this.getHeatmapRenderer(heatmapRenderer);
                (layer as IMapcoreFeatureLayer).mapcoreProps.renderer = newLayer.renderer;
            }
            (layer as IMapcoreFeatureLayer).renderer = layer.mapcoreProps.heatmapRenderer;
        } else if (layer && heatmapMode === HeatmapMode.Auto) {
            layer.mapcoreProps.heatmapMode = heatmapMode;
            const heatmapOn = this.layerService.checkIfHeatmapIsOnOrOff(layer);
            if (heatmapOn) {
                (layer as IMapcoreFeatureLayer).renderer = layer.mapcoreProps.heatmapRenderer;
            } else {
                (layer as IMapcoreFeatureLayer).renderer = layer.mapcoreProps.renderer;
            }
        }
        return layer;
    }

    public drawScene(surface: Surface, startColor, endColor) {
        surface.clear();
        const gradient = new LinearGradient({
            name: 'grd',
            stops: [
                new GradientStop(0, startColor, 1),
                new GradientStop(1, endColor, 1)
            ]
        });

        // This rectangle demonstrates how to use the gradient as a fill.
        const rect = new RectGeometry(
            new Point(0, 0),
            new Size(400, 36)
        );

        const drawingRect = new Rect(rect, {
            stroke: {
                color: '#9999b6',
                width: 1
            },
            fill: gradient as any // fill accepts gradients but not supported in Kendo typings
        });

        // Place all the shapes in a group.
        const group = new Group();
        group.append(drawingRect);

        // Translate the group.
        group.transform(
            transform().translate(5, 0)
        );

        // Render the group on the surface.
        surface.draw(group);
    }
}
